import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  MapPin, Clock, AlertTriangle, User, Eye, Share2, 
  ChevronLeft, Calendar, Phone, Mail, QrCode, Download,
  Facebook, Twitter, ExternalLink, CheckCircle, Edit, Trash2, ShieldAlert, Flag
} from 'lucide-react';
import axios from 'axios';
import ReportAbuseDialog from '../components/ReportAbuseDialog';
import { FEATURES } from '../config/features';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_locate-loved-ones-3/artifacts/5wufybp4_LOGO.jpeg";

export default function CaseDetailPage() {
  const { caseId } = useParams();
  const { user, token, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [sightings, setSightings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showShareOptions, setShowShareOptions] = useState(false);
  const [abuseDialogOpen, setAbuseDialogOpen] = useState(false);
  const [appealOpen, setAppealOpen] = useState(false);
  const [appealDoc, setAppealDoc] = useState('');
  const [appealDocName, setAppealDocName] = useState('');
  const [appealDocType, setAppealDocType] = useState('');
  const [appealNote, setAppealNote] = useState('');
  const [appealError, setAppealError] = useState('');
  const [appealSubmitting, setAppealSubmitting] = useState(false);
  const [evidencePackLoading, setEvidencePackLoading] = useState(false);
  const [evidencePackError, setEvidencePackError] = useState('');

  const handleDownloadEvidencePack = async () => {
    setEvidencePackError('');
    setEvidencePackLoading(true);
    try {
      const res = await axios.get(
        `${API}/admin/cases/${caseId}/evidence-pack`,
        {
          responseType: 'blob',
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        }
      );
      // Extract filename from Content-Disposition if present, else fall back.
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/i);
      const filename = match ? match[1] : `misyou-evidence-${caseId}.zip`;

      const blobUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/zip' }));
      const anchor = document.createElement('a');
      anchor.href = blobUrl;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      const detail = err?.response?.data;
      // If the server sent back a JSON error inside the blob, decode it.
      if (detail instanceof Blob) {
        try {
          const txt = await detail.text();
          const parsed = JSON.parse(txt);
          setEvidencePackError(parsed.detail || 'Failed to download evidence pack.');
        } catch (_) {
          setEvidencePackError('Failed to download evidence pack.');
        }
      } else {
        setEvidencePackError(detail?.detail || 'Failed to download evidence pack.');
      }
    } finally {
      setEvidencePackLoading(false);
    }
  };

  useEffect(() => {
    fetchCaseData();
    fetchSightings();
  }, [caseId]);

  const fetchCaseData = async () => {
    try {
      const response = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(response.data);
    } catch (error) {
      console.error('Failed to fetch case:', error);
      navigate('/search');
    } finally {
      setLoading(false);
    }
  };

  const fetchSightings = async () => {
    try {
      const response = await axios.get(`${API}/sightings/${caseId}`);
      setSightings(response.data);
    } catch (error) {
      console.error('Failed to fetch sightings:', error);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await axios.patch(`${API}/cases/${caseId}/status?status=${newStatus}`, {}, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setCaseData({ ...caseData, status: newStatus });
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this case?')) return;
    
    try {
      await axios.delete(`${API}/cases/${caseId}`, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      navigate('/dashboard');
    } catch (error) {
      console.error('Failed to delete case:', error);
    }
  };

  const handleAppealSubmit = async () => {
    setAppealError('');
    if (!appealDoc) {
      setAppealError('Please attach a clearer guardianship document before submitting.');
      return;
    }
    setAppealSubmitting(true);
    try {
      await axios.post(`${API}/cases/${caseId}/guardianship/appeal`, {
        guardianship_document: appealDoc,
        guardianship_document_type: appealDocType,
        appeal_note: appealNote,
      }, {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });
      setAppealOpen(false);
      setAppealDoc('');
      setAppealDocName('');
      setAppealDocType('');
      setAppealNote('');
      await fetchCaseData();
    } catch (error) {
      setAppealError(error?.response?.data?.detail || 'Failed to submit appeal. Please try again.');
    } finally {
      setAppealSubmitting(false);
    }
  };

  const shareToSocial = (platform) => {
    const url = window.location.href;
    const text = `Help find ${caseData?.full_name}! Last seen in ${caseData?.last_seen_location?.city || 'unknown location'}. Please share!`;
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
      copy: () => {
        navigator.clipboard.writeText(`${text} ${url}`);
        alert('Link copied to clipboard!');
      }
    };

    if (platform === 'copy') {
      shareUrls.copy();
    } else {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
    setShowShareOptions(false);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Unknown';
    return new Date(dateStr).toLocaleDateString('en-US', { 
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' 
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      active: { label: 'MISSING', class: 'bg-red-600 text-white border-0', pulse: true },
      under_investigation: { label: 'Under Investigation', class: 'bg-amber-500 text-white border-0' },
      sighting_confirmed: { label: 'Sighting Confirmed', class: 'bg-blue-600 text-white border-0' },
      located_safe: { label: 'FOUND SAFE', class: 'bg-green-600 text-white border-0' },
      closed: { label: 'Closed', class: 'bg-slate-500 text-white border-0' }
    };
    const config = statusConfig[status] || statusConfig.active;
    return (
      <Badge className={`${config.class} ${config.pulse ? 'status-active' : ''} px-4 py-1.5 text-sm`}>
        {config.label}
      </Badge>
    );
  };

  const isOwner = user && caseData && (user.user_id === caseData.reported_by || user.role === 'admin');

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  if (!caseData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <p className="text-muted-foreground">Case not found</p>
          <Link to="/search">
            <Button className="mt-4">Back to Search</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link to="/search" className="text-muted-foreground hover:text-primary">
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <Link to="/" className="flex items-center gap-2">
                <img src={LOGO_URL} alt="MISYOU" className="w-8 h-8 rounded-lg object-cover" />
                <span className="text-lg font-black text-slate-900 hidden sm:block">MISYOU</span>
              </Link>
            </div>
            
            <div className="flex items-center gap-2">
              <div className="relative">
                <Button 
                  variant="outline" 
                  onClick={() => setShowShareOptions(!showShareOptions)}
                  data-testid="share-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
                {showShareOptions && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-slate-200 py-2 z-50">
                    <button 
                      onClick={() => shareToSocial('facebook')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                    >
                      <Facebook className="w-4 h-4 text-blue-600" />
                      Facebook
                    </button>
                    <button 
                      onClick={() => shareToSocial('twitter')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                    >
                      <Twitter className="w-4 h-4 text-sky-500" />
                      Twitter / X
                    </button>
                    <button 
                      onClick={() => shareToSocial('copy')}
                      className="w-full px-4 py-2 text-left hover:bg-slate-50 flex items-center gap-2"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Copy Link
                    </button>
                  </div>
                )}
              </div>
              <Link to={`/submit-sighting/${caseId}`}>
                <Button className="bg-red-600 hover:bg-red-700 rounded-full" data-testid="report-sighting-btn">
                  <Eye className="w-4 h-4 mr-2" />
                  Report Sighting
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Photo & Status */}
            <Card className="overflow-hidden bg-white shadow-xl" data-testid="case-detail-card">
              <div className="relative">
                {caseData.photos && caseData.photos.length > 0 ? (
                  <img 
                    src={caseData.photos[0]} 
                    alt={caseData.full_name}
                    className="w-full h-96 object-cover"
                  />
                ) : (
                  <div className="w-full h-96 bg-slate-200 flex items-center justify-center">
                    <User className="w-24 h-24 text-slate-400" />
                  </div>
                )}
                <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                  {getStatusBadge(caseData.status)}
                  {caseData.is_urgent && (
                    <Badge className="bg-amber-500 text-white border-0 px-3 py-1">
                      <AlertTriangle className="w-3 h-3 mr-1" />
                      URGENT
                    </Badge>
                  )}
                  {caseData.is_child && (
                    <Badge className="bg-purple-600 text-white border-0 px-3 py-1">CHILD</Badge>
                  )}
                  {caseData.is_elderly && (
                    <Badge className="bg-orange-600 text-white border-0 px-3 py-1">ELDERLY</Badge>
                  )}
                  {caseData.guardianship_review_status === 'pending' && (
                    <Badge
                      className="bg-indigo-100 text-indigo-800 border border-indigo-300 px-3 py-1"
                      data-testid="guardianship-pending-badge"
                    >
                      <ShieldAlert className="w-3 h-3 mr-1" />
                      Guardianship under review
                    </Badge>
                  )}
                  {caseData.guardianship_review_status === 'approved' && (
                    <Badge
                      className="bg-emerald-100 text-emerald-800 border border-emerald-300 px-3 py-1"
                      data-testid="guardianship-approved-badge"
                    >
                      <CheckCircle className="w-3 h-3 mr-1" />
                      Guardianship verified
                    </Badge>
                  )}
                  {caseData.guardianship_review_status === 'rejected' && (
                    <Badge
                      className="bg-red-100 text-red-700 border border-red-300 px-3 py-1"
                      data-testid="guardianship-rejected-badge"
                    >
                      Guardianship rejected
                    </Badge>
                  )}
                </div>
              </div>
              
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h1 className="text-3xl font-black text-slate-900">{caseData.full_name}</h1>
                    <p className="text-muted-foreground mono text-sm mt-1">Case ID: {caseData.case_id}</p>
                  </div>
                  {FEATURES.SHOW_REWARDS && caseData.reward_amount && (
                    <Badge className="bg-green-100 text-green-700 text-lg px-4 py-2">
                      Reward: ₹{caseData.reward_amount}
                    </Badge>
                  )}
                </div>

                {/* Admin-only: download a signed evidence pack for police handoff. */}
                {user?.role === 'admin' && (
                  <div
                    className="mb-6 rounded-xl border border-slate-300 bg-slate-50 p-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                    data-testid="evidence-pack-admin-card"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-900 text-white flex items-center justify-center shrink-0">
                        <Download className="w-5 h-5" />
                      </div>
                      <div>
                        <p className="font-semibold text-slate-900">Police evidence pack</p>
                        <p className="text-xs text-slate-600 mt-0.5">
                          One-click ZIP containing case details, decrypted FIR + guardianship
                          docs, photos, sightings, and full audit trail. SHA-256 signed. Every
                          download is audit-logged.
                        </p>
                      </div>
                    </div>
                    <Button
                      onClick={handleDownloadEvidencePack}
                      disabled={evidencePackLoading}
                      className="bg-slate-900 hover:bg-slate-800 text-white shrink-0"
                      data-testid="download-evidence-pack-btn"
                    >
                      {evidencePackLoading ? 'Preparing…' : (
                        <>
                          <Download className="w-4 h-4 mr-2" />
                          Download evidence pack (.zip)
                        </>
                      )}
                    </Button>
                    {evidencePackError && (
                      <div
                        className="text-sm text-red-700 basis-full"
                        data-testid="evidence-pack-error"
                      >
                        {evidencePackError}
                      </div>
                    )}
                  </div>
                )}

                {/* Anti-scam safety banner — informs viewers + families about extortion attempts */}
                <div
                  className="mb-6 rounded-xl border-2 border-red-300 bg-gradient-to-br from-red-50 to-amber-50 p-5 shadow-sm"
                  data-testid="anti-scam-banner"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-red-100 text-red-700 flex items-center justify-center shrink-0">
                      <ShieldAlert className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-red-900 text-base">Beware of scams</p>
                      <p className="text-sm text-red-800 mt-1">
                        Anyone demanding <strong>money in exchange for information</strong> about this person is a scammer.
                        Real authorities (police, hospitals) will <strong>never</strong> ask for payment.
                      </p>
                      <p className="mt-3 text-base sm:text-lg font-bold text-red-900 leading-snug bg-white/60 border-l-4 border-red-600 px-3 py-2 rounded">
                        MISYOU advises you — Don&apos;t give any money to anyone without involving your local police station, always keep your local police informed.
                      </p>
                      <Button
                        size="sm"
                        onClick={() => setAbuseDialogOpen(true)}
                        className="mt-3 bg-red-600 hover:bg-red-700 text-white"
                        data-testid="report-abuse-trigger-btn"
                      >
                        <Flag className="w-4 h-4 mr-2" />
                        Report a scam / abuse
                      </Button>
                    </div>
                  </div>
                </div>
                <ReportAbuseDialog
                  open={abuseDialogOpen}
                  onOpenChange={setAbuseDialogOpen}
                  caseId={caseData.case_id}
                  token={token}
                />

                {/* How to verify a real sighting — guards families against scams */}
                <div
                  className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50/60 p-5"
                  data-testid="verify-sighting-guide"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                      <CheckCircle className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-bold text-emerald-900 text-base">How to verify a real sighting</p>
                      <p className="text-sm text-emerald-800/90 mt-1">
                        Most genuine tips come from strangers nearby — not strangers asking for money.
                        Use this quick checklist before reacting to any caller or message:
                      </p>
                      <ul className="mt-3 space-y-2 text-sm text-emerald-900">
                        <li className="flex gap-2">
                          <span className="font-bold text-emerald-700">1.</span>
                          <span>Ask for a <strong>recent photo</strong> or a clear description of clothing, scars, or birthmarks not shown in the public listing.</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-bold text-emerald-700">2.</span>
                          <span>Ask for the <strong>exact location</strong> (street, landmark, city) and the <strong>date &amp; time</strong> they were seen.</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-bold text-emerald-700">3.</span>
                          <span>Cross-check the details with your <strong>local police station</strong> before travelling or sharing personal info.</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-bold text-emerald-700">4.</span>
                          <span><strong>Never send money</strong> or share OTPs / bank details. Genuine helpers, police, and hospitals will never ask for payment.</span>
                        </li>
                        <li className="flex gap-2">
                          <span className="font-bold text-emerald-700">5.</span>
                          <span>Prefer tips submitted through the in-app <strong>Submit a Sighting</strong> form — they are logged, timestamped, and reviewable by our moderators.</span>
                        </li>
                      </ul>
                      <p className="mt-3 text-xs text-emerald-900/80">
                        If anything feels off, tap <strong>“Report a scam / abuse”</strong> above. We act fast and may permanently ban the offender.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  {caseData.age && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground uppercase">Age</p>
                      <p className="font-semibold text-slate-900">{caseData.age} years</p>
                    </div>
                  )}
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground uppercase">Gender</p>
                    <p className="font-semibold text-slate-900 capitalize">{caseData.gender}</p>
                  </div>
                  <div className="bg-slate-50 p-3 rounded-lg">
                    <p className="text-xs text-muted-foreground uppercase">Sightings</p>
                    <p className="font-semibold text-slate-900">{caseData.sightings_count}</p>
                  </div>
                  {caseData.date_of_birth && (
                    <div className="bg-slate-50 p-3 rounded-lg">
                      <p className="text-xs text-muted-foreground uppercase">DOB</p>
                      <p className="font-semibold text-slate-900">{formatDate(caseData.date_of_birth)}</p>
                    </div>
                  )}
                </div>

                {/* Physical Description */}
                {caseData.physical_description && (
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-3">Physical Description</h3>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {caseData.physical_description.height && (
                        <div><span className="text-muted-foreground">Height:</span> {caseData.physical_description.height}</div>
                      )}
                      {caseData.physical_description.weight && (
                        <div><span className="text-muted-foreground">Weight:</span> {caseData.physical_description.weight}</div>
                      )}
                      {caseData.physical_description.hair_color && (
                        <div><span className="text-muted-foreground">Hair:</span> {caseData.physical_description.hair_color}</div>
                      )}
                      {caseData.physical_description.eye_color && (
                        <div><span className="text-muted-foreground">Eyes:</span> {caseData.physical_description.eye_color}</div>
                      )}
                      {caseData.physical_description.build && (
                        <div><span className="text-muted-foreground">Build:</span> {caseData.physical_description.build}</div>
                      )}
                    </div>
                    {caseData.physical_description.distinguishing_marks && (
                      <p className="mt-2">
                        <span className="text-muted-foreground">Distinguishing Marks:</span> {caseData.physical_description.distinguishing_marks}
                      </p>
                    )}
                  </div>
                )}

                {/* Last Seen */}
                <div className="mb-6">
                  <h3 className="font-bold text-slate-900 mb-3">Last Seen</h3>
                  <div className="space-y-2">
                    {caseData.last_seen_location && (
                      <div className="flex items-start gap-2">
                        <MapPin className="w-5 h-5 text-red-600 mt-0.5" />
                        <div>
                          {caseData.last_seen_location.address && <p>{caseData.last_seen_location.address}</p>}
                          <p>
                            {[caseData.last_seen_location.city, caseData.last_seen_location.state, caseData.last_seen_location.country]
                              .filter(Boolean).join(', ')}
                          </p>
                        </div>
                      </div>
                    )}
                    {caseData.last_seen_date && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-5 h-5 text-primary" />
                        <span>{formatDate(caseData.last_seen_date)} {caseData.last_seen_time && `at ${caseData.last_seen_time}`}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Clothing */}
                {caseData.clothing_description && (
                  <div className="mb-6">
                    <h3 className="font-bold text-slate-900 mb-2">Clothing Description</h3>
                    <p className="text-slate-700">{caseData.clothing_description}</p>
                  </div>
                )}

                {/* Medical Conditions */}
                {caseData.medical_conditions && (
                  <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                    <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" />
                      Medical Conditions
                    </h3>
                    <p className="text-amber-700">{caseData.medical_conditions}</p>
                  </div>
                )}

                {/* Additional Info */}
                {caseData.additional_info && (
                  <div>
                    <h3 className="font-bold text-slate-900 mb-2">Additional Information</h3>
                    <p className="text-slate-700">{caseData.additional_info}</p>
                  </div>
                )}

                {/* Reporter declarations & legal verification */}
                {(caseData.reporter_relationship
                  || caseData.guardianship_review_status
                  || caseData.is_legal_guardian_declared
                  || caseData.not_voluntarily_missing_declared
                  || caseData.police_station) && (
                  <div
                    className="mt-6 rounded-xl border border-slate-200 bg-slate-50/70 p-4"
                    data-testid="reporter-declarations-block"
                  >
                    <h3 className="font-bold text-slate-900 mb-2 text-sm flex items-center gap-2">
                      <ShieldAlert className="w-4 h-4 text-indigo-600" />
                      Reporter declarations &amp; verification
                    </h3>
                    <dl className="text-sm space-y-1 text-slate-700">
                      {caseData.reporter_relationship && (
                        <div className="flex gap-2">
                          <dt className="font-semibold text-slate-600">Relationship:</dt>
                          <dd className="capitalize">
                            {String(caseData.reporter_relationship).replaceAll('_', ' ')}
                          </dd>
                        </div>
                      )}
                      {caseData.police_station && (
                        <div className="flex gap-2">
                          <dt className="font-semibold text-slate-600">Police station:</dt>
                          <dd>{caseData.police_station}</dd>
                        </div>
                      )}
                      {caseData.police_reporting_date && (
                        <div className="flex gap-2">
                          <dt className="font-semibold text-slate-600">FIR reported:</dt>
                          <dd>{caseData.police_reporting_date}</dd>
                        </div>
                      )}
                      {caseData.fir_number && (
                        <div className="flex gap-2">
                          <dt className="font-semibold text-slate-600">FIR No.:</dt>
                          <dd className="mono">{caseData.fir_number}</dd>
                        </div>
                      )}
                      {caseData.is_legal_guardian_declared && (
                        <div className="flex gap-2 text-emerald-700">
                          <CheckCircle className="w-4 h-4 mt-0.5" />
                          <dd>Reporter declared themselves as the legal guardian of the child.</dd>
                        </div>
                      )}
                      {caseData.not_voluntarily_missing_declared && (
                        <div className="flex gap-2 text-emerald-700">
                          <CheckCircle className="w-4 h-4 mt-0.5" />
                          <dd>Reporter declared the adult did not leave voluntarily.</dd>
                        </div>
                      )}
                      {caseData.guardianship_review_status === 'pending' && (
                        <div className="flex gap-2 text-indigo-700 mt-2">
                          <ShieldAlert className="w-4 h-4 mt-0.5" />
                          <dd>Guardianship documents pending moderator review. This is a normal step for under-18 cases.</dd>
                        </div>
                      )}
                      {caseData.guardianship_review_status === 'approved' && (
                        <div className="flex gap-2 text-emerald-700 mt-2">
                          <CheckCircle className="w-4 h-4 mt-0.5" />
                          <dd>Guardianship verified by a MISYOU moderator.</dd>
                        </div>
                      )}
                      {caseData.guardianship_review_status === 'rejected' && (
                        <div className="flex gap-2 text-red-700 mt-2">
                          <AlertTriangle className="w-4 h-4 mt-0.5" />
                          <dd>Guardianship could not be verified. This case has been withdrawn from public listings.</dd>
                        </div>
                      )}
                    </dl>

                    {/* Owner-only appeal CTA — gives a genuine parent who uploaded
                        the wrong document a one-click path back into the queue. */}
                    {caseData.guardianship_review_status === 'rejected'
                      && user
                      && caseData.reported_by === user.user_id
                      && (caseData.guardianship_appeals_count || 0) < 2 && (
                      <div
                        className="mt-4 rounded-lg border-2 border-amber-300 bg-amber-50 p-4"
                        data-testid="guardianship-appeal-cta"
                      >
                        <p className="text-sm font-bold text-amber-900 mb-1 flex items-center gap-2">
                          <ShieldAlert className="w-4 h-4" />
                          Disagree with this decision?
                        </p>
                        {caseData.guardianship_rejection_reason && (
                          <p className="text-xs text-amber-800 mb-2">
                            <strong>Moderator's reason:</strong> {caseData.guardianship_rejection_reason}
                          </p>
                        )}
                        <p className="text-xs text-amber-800 mb-3">
                          Upload a clearer document or a different proof of guardianship and your case will be put back under review immediately.
                          {typeof caseData.guardianship_appeals_count === 'number' && (
                            <> You have <strong>{2 - (caseData.guardianship_appeals_count || 0)}</strong> appeal{2 - (caseData.guardianship_appeals_count || 0) === 1 ? '' : 's'} left.</>
                          )}
                        </p>
                        <Button
                          size="sm"
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          onClick={() => {
                            setAppealError('');
                            setAppealDoc('');
                            setAppealDocName('');
                            setAppealDocType('');
                            setAppealNote('');
                            setAppealOpen(true);
                          }}
                          data-testid="open-guardianship-appeal-btn"
                        >
                          Appeal &amp; re-upload document
                        </Button>
                      </div>
                    )}
                    {caseData.guardianship_review_status === 'rejected'
                      && user
                      && caseData.reported_by === user.user_id
                      && (caseData.guardianship_appeals_count || 0) >= 2 && (
                      <p
                        className="mt-3 text-xs text-red-700"
                        data-testid="guardianship-appeal-exhausted"
                      >
                        You have used all 2 appeals on this case. If you believe this is a mistake, please email
                        {' '}<a href="mailto:contact@misyou.in" className="underline">contact@misyou.in</a>.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Sightings Timeline */}
            <Card className="bg-white shadow-lg">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5 text-primary" />
                  Sightings ({sightings.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                {sightings.length === 0 ? (
                  <div className="text-center py-8">
                    <Eye className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-muted-foreground">No sightings reported yet</p>
                    <Link to={`/submit-sighting/${caseId}`}>
                      <Button className="mt-4 rounded-full">Report a Sighting</Button>
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {sightings.map((sighting, index) => (
                      <div key={sighting.sighting_id} className="relative pl-6 pb-4 border-l-2 border-slate-200 last:border-0">
                        <div className="absolute left-[-9px] top-0 w-4 h-4 rounded-full bg-primary border-2 border-white"></div>
                        <div className="bg-slate-50 p-4 rounded-lg">
                          <div className="flex items-center gap-2 mb-2">
                            {sighting.verified && (
                              <Badge className="bg-green-100 text-green-700">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                Verified
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground mono">
                              {formatDate(sighting.created_at)}
                            </span>
                          </div>
                          <p className="text-slate-700">{sighting.description}</p>
                          {sighting.location && (
                            <p className="text-sm text-muted-foreground mt-2 flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {[sighting.location.city, sighting.location.state].filter(Boolean).join(', ')}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Contact Card — phone/email are visible ONLY to the case owner / admin / moderator.
                Public viewers must use the in-app "Submit a Sighting" flow to reach the family.
                This prevents extortion scammers from harvesting direct contact details. */}
            {(caseData.contact_phone || caseData.contact_email) &&
              (user?.role === 'admin' || user?.role === 'moderator' || user?.user_id === caseData.reported_by) && (
              <Card className="bg-white shadow-lg" data-testid="private-contact-card">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    Contact Information
                    <Badge className="bg-slate-100 text-slate-700 text-[10px] ml-auto">Private</Badge>
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">Visible only to you and our moderation team.</p>
                </CardHeader>
                <CardContent className="space-y-3">
                  {caseData.contact_phone && (
                    <a 
                      href={`tel:${caseData.contact_phone}`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <Phone className="w-5 h-5 text-primary" />
                      <span className="font-medium">{caseData.contact_phone}</span>
                    </a>
                  )}
                  {caseData.contact_email && (
                    <a 
                      href={`mailto:${caseData.contact_email}`}
                      className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <Mail className="w-5 h-5 text-primary" />
                      <span className="font-medium">{caseData.contact_email}</span>
                    </a>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Public reach-out card — shown to everyone else.  No raw phone exposed. */}
            {!(user?.role === 'admin' || user?.role === 'moderator' || user?.user_id === caseData.reported_by) && (
              <Card className="bg-white shadow-lg" data-testid="public-contact-card">
                <CardHeader>
                  <CardTitle className="text-lg">Contact the family safely</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-slate-600 mb-4">
                    For everyone&apos;s safety, the family&apos;s phone number is hidden. Use the
                    secure <strong>Submit a Sighting</strong> form to share any information &mdash; the
                    family is notified immediately.
                  </p>
                  <Link to={`/submit-sighting/${caseId}`} className="block">
                    <Button className="w-full" size="lg" data-testid="public-contact-submit-sighting-btn">
                      <Eye className="w-4 h-4 mr-2" />
                      Submit a Sighting
                    </Button>
                  </Link>
                  <Button
                    variant="outline"
                    size="sm"
                    className="w-full mt-2 text-red-600 border-red-200 hover:bg-red-50"
                    onClick={() => setAbuseDialogOpen(true)}
                    data-testid="public-contact-report-abuse-btn"
                  >
                    <Flag className="w-4 h-4 mr-2" />
                    Report a scam call
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Report Sighting CTA */}
            <Card className="bg-red-600 text-white shadow-lg">
              <CardContent className="p-6 text-center">
                <Eye className="w-12 h-12 mx-auto mb-4 opacity-90" />
                <h3 className="text-xl font-bold mb-2">Have you seen {caseData.full_name.split(' ')[0]}?</h3>
                <p className="text-red-100 mb-4">Your information could help bring them home.</p>
                <Link to={`/submit-sighting/${caseId}`}>
                  <Button className="w-full bg-white text-red-600 hover:bg-red-50 rounded-full btn-press">
                    Report a Sighting
                  </Button>
                </Link>
              </CardContent>
            </Card>

            {/* Case Actions (Owner Only) */}
            {isOwner && (
              <Card className="bg-white shadow-lg">
                <CardHeader>
                  <CardTitle className="text-lg">Case Management</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="space-y-2">
                    <label className="text-sm text-muted-foreground">Update Status</label>
                    <select 
                      className="w-full p-2 border border-slate-200 rounded-lg"
                      value={caseData.status}
                      onChange={(e) => handleStatusChange(e.target.value)}
                      data-testid="status-select"
                    >
                      <option value="active">Active</option>
                      <option value="under_investigation">Under Investigation</option>
                      <option value="sighting_confirmed">Sighting Confirmed</option>
                      <option value="located_safe">Located Safe</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <Separator />
                  <Button 
                    variant="destructive" 
                    className="w-full"
                    onClick={handleDelete}
                    data-testid="delete-case-btn"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Case
                  </Button>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats */}
            <Card className="bg-white shadow-lg">
              <CardContent className="p-4">
                <div className="text-center">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider">Case Reported</p>
                  <p className="font-semibold text-slate-900 mono text-sm">{formatDate(caseData.created_at)}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      {/* Guardianship Appeal Dialog (owner only) */}
      <Dialog open={appealOpen} onOpenChange={setAppealOpen}>
        <DialogContent className="max-w-lg" data-testid="guardianship-appeal-dialog">
          <DialogHeader>
            <DialogTitle className="text-xl">Appeal guardianship rejection</DialogTitle>
            <DialogDescription>
              Upload a clearer document and your case will go back into the moderator review queue immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {caseData?.guardianship_rejection_reason && (
              <div className="rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                <strong>Previous rejection reason:</strong> {caseData.guardianship_rejection_reason}
              </div>
            )}
            <div>
              <Label htmlFor="appeal-doc-type">Type of guardianship proof</Label>
              <Select value={appealDocType} onValueChange={setAppealDocType}>
                <SelectTrigger className="mt-1" data-testid="appeal-doc-type-select">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="birth_certificate">Birth certificate</SelectItem>
                  <SelectItem value="aadhaar">Aadhaar (with parent name)</SelectItem>
                  <SelectItem value="school_id">School ID with parent name</SelectItem>
                  <SelectItem value="court_order">Court guardianship order</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="appeal-doc">New document (JPG / PNG / WEBP / PDF, max 5 MB)</Label>
              <Input
                id="appeal-doc"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp,application/pdf"
                onChange={(e) => {
                  const file = e.target.files && e.target.files[0];
                  if (!file) {
                    setAppealDoc('');
                    setAppealDocName('');
                    return;
                  }
                  if (file.size > 5 * 1024 * 1024) {
                    setAppealError('File is too large. Please upload a file under 5 MB.');
                    e.target.value = '';
                    return;
                  }
                  const reader = new FileReader();
                  reader.onloadend = () => {
                    setAppealDoc(reader.result || '');
                    setAppealDocName(file.name);
                    setAppealError('');
                  };
                  reader.readAsDataURL(file);
                }}
                data-testid="appeal-doc-input"
              />
              {appealDocName && (
                <p className="text-xs text-emerald-700 mt-1">Attached: {appealDocName}</p>
              )}
            </div>
            <div>
              <Label htmlFor="appeal-note">Note to the moderator (optional, max 500 chars)</Label>
              <Textarea
                id="appeal-note"
                value={appealNote}
                onChange={(e) => setAppealNote(e.target.value.slice(0, 500))}
                placeholder="E.g., The earlier upload showed only the back of my Aadhaar — this one shows both pages with my child's name."
                rows={3}
                className="mt-1"
                data-testid="appeal-note-textarea"
              />
              <p className="text-xs text-muted-foreground mt-1">{appealNote.length}/500</p>
            </div>
            {appealError && (
              <p className="text-sm text-red-600" data-testid="appeal-error">{appealError}</p>
            )}
            <div className="flex gap-3 pt-2">
              <Button
                variant="outline"
                onClick={() => setAppealOpen(false)}
                className="flex-1"
                disabled={appealSubmitting}
              >
                Cancel
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={handleAppealSubmit}
                disabled={appealSubmitting || !appealDoc}
                data-testid="submit-guardianship-appeal-btn"
              >
                {appealSubmitting ? 'Submitting…' : 'Submit appeal'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
